import { APP_NAME } from '../utils/branding';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaDownload,
  FaImages,
  FaReceipt,
  FaSearch,
  FaTimesCircle,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { ownsEvents } from '../utils/roles';
import {
  downloadPhoto,
  downloadReceipt,
  getMyPurchases,
  getPhotographerPaymentHistory,
} from '../services/api';
import './PaymentHistory.css';

/** Payments listed per page. */
const PAYMENTS_PER_PAGE = 10;

const getPhotoName = (purchase) => {
  const photo = purchase?.photo || {};
  const name = photo.name || photo.title || photo.filename || `Photo ${purchase?.photo_id || ''}`;
  return String(name).split('/').pop() || name;
};

const getEventName = (purchase) => purchase?.photo?.event?.name || 'Unassigned event';

const formatAmount = (amount) => `${Number(amount || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} F`;

const formatDate = (date) => {
  if (!date) return 'Not available';
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * The payment's time of day, in GMT.
 *
 * The API stores and serialises timestamps in UTC (the trailing "Z"), so this
 * forces UTC rather than the viewer's own zone: a buyer and a photographer in
 * two different countries then quote the same time for the same payment, which
 * is what makes it usable when reconciling against a provider's records.
 */
const formatTimeGMT = (date) => {
  if (!date) return null;

  const time = new Date(date).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });

  return `${time} GMT`;
};

const getPaymentLabel = (transactionId) => {
  const value = String(transactionId || '').toUpperCase();
  if (value.startsWith('WAVE-CI')) return 'Wave CI';
  if (value.startsWith('OM-CI')) return 'Orange Money CI';
  if (value.startsWith('VISA')) return 'Visa';
  if (value.startsWith('PM')) return 'CinetPay';
  if (value.startsWith('TXN')) return 'Photo purchase';
  return 'Payment';
};

const statusMeta = {
  completed: { label: 'Completed', icon: FaCheckCircle },
  pending: { label: 'Pending', icon: FaClock },
  failed: { label: 'Failed', icon: FaTimesCircle },
};

const normalizeStatus = (status) => String(status || 'pending').toLowerCase();

const getPaymentTransactionId = (purchase) => {
  const transactionId = String(purchase?.transaction_id || '').trim();

  if (transactionId && purchase?.photo_id && transactionId.endsWith(`-${purchase.photo_id}`)) {
    return transactionId.slice(0, -(String(purchase.photo_id).length + 1));
  }

  return transactionId || `purchase-${purchase?.id || purchase?.photo_id || 'unknown'}`;
};

const getPaymentStatus = (purchases) => {
  const statuses = purchases.map(item => normalizeStatus(item.payment_status));

  if (statuses.every(status => status === 'completed')) return 'completed';
  if (statuses.some(status => status === 'pending')) return 'pending';
  if (statuses.every(status => status === 'failed')) return 'failed';
  return statuses[0] || 'pending';
};

const buildPaymentRows = (purchases) => {
  const groups = new Map();

  purchases.forEach((purchase) => {
    const paymentId = getPaymentTransactionId(purchase);
    const key = `${paymentId}-${purchase?.user_id || purchase?.user?.id || 'me'}`;

    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        transactionId: paymentId,
        purchases: [],
      });
    }

    groups.get(key).purchases.push(purchase);
  });

  return Array.from(groups.values()).map((payment) => {
    const purchasesInPayment = payment.purchases;
    const firstPurchase = purchasesInPayment[0] || {};
    const photoNames = purchasesInPayment.map(getPhotoName);
    const eventNames = Array.from(new Set(purchasesInPayment.map(getEventName)));
    const sortedDates = purchasesInPayment
      .map(item => item.created_at)
      .filter(Boolean)
      .sort();
    const latestDate = sortedDates[sortedDates.length - 1];

    return {
      ...payment,
      firstPurchase,
      photoNames,
      eventNames,
      status: getPaymentStatus(purchasesInPayment),
      netAmount: purchasesInPayment.reduce((sum, item) => sum + Number(item.net_amount ?? item.amount ?? 0), 0),
      commissionAmount: purchasesInPayment.reduce((sum, item) => sum + Number(item.commission_amount || 0), 0),
      amount: purchasesInPayment.reduce((sum, item) => sum + Number(item.amount || 0), 0),
      createdAt: latestDate || firstPurchase.created_at,
      buyer: firstPurchase.user,
    };
  }).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
};

function PaymentHistory() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingReceiptId, setDownloadingReceiptId] = useState(null);
  const [expandedPaymentId, setExpandedPaymentId] = useState(null);
  const [page, setPage] = useState(1);

  const isPhotographer = ownsEvents(user);

  useEffect(() => {
    const loadPaymentHistory = async () => {
      setLoading(true);
      setError('');

      try {
        const response = isPhotographer
          ? await getPhotographerPaymentHistory()
          : await getMyPurchases();

        const nextRecords = isPhotographer
          ? response.sales || response.data || []
          : response.purchases || response.data || [];

        setRecords(Array.isArray(nextRecords) ? nextRecords : []);
        setSummary(response.summary || null);
      } catch (err) {
        setError(err.message || 'Failed to load payment history');
      } finally {
        setLoading(false);
      }
    };

    loadPaymentHistory();
  }, [isPhotographer]);

  const paymentRows = useMemo(() => buildPaymentRows(records), [records]);

  const totals = useMemo(() => {
    const completedPayments = paymentRows.filter(item => item.status === 'completed');
    const completedPurchases = records.filter(item => normalizeStatus(item.payment_status) === 'completed');

    return {
      count: paymentRows.length,
      completed: completedPayments.length,
      total: completedPurchases.reduce((sum, item) => sum + Number(isPhotographer ? (item.net_amount ?? item.amount ?? 0) : (item.amount || 0)), 0),
    };
  }, [paymentRows, records, isPhotographer]);

  const filteredRecords = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return paymentRows.filter((payment) => {
      const status = payment.status;
      const matchesStatus = statusFilter === 'all' || status === statusFilter;
      const searchText = [
        ...payment.photoNames,
        ...payment.eventNames,
        payment.transactionId,
        ...payment.purchases.map(item => item.transaction_id),
        payment.buyer?.name,
        payment.buyer?.email,
      ].filter(Boolean).join(' ').toLowerCase();

      return matchesStatus && (!normalizedQuery || searchText.includes(normalizedQuery));
    });
  }, [paymentRows, query, statusFilter]);

  /*
   * Pagination is done here rather than by the API because a "payment" is
   * assembled client-side from several purchases sharing a transaction: paging
   * the raw purchases server-side would split one payment across two pages.
   */
  const pageCount = Math.max(1, Math.ceil(filteredRecords.length / PAYMENTS_PER_PAGE));

  /*
   * A narrowed search can leave `page` past the end of the results. Clamping
   * as it is read, rather than only in an effect, keeps the table from
   * rendering one empty frame before the effect catches up.
   */
  const currentPage = Math.min(page, pageCount);

  useEffect(() => {
    setPage(current => Math.min(current, pageCount));
  }, [pageCount]);

  const pagedRecords = useMemo(() => {
    const start = (currentPage - 1) * PAYMENTS_PER_PAGE;
    return filteredRecords.slice(start, start + PAYMENTS_PER_PAGE);
  }, [filteredRecords, currentPage]);

  // A new search or status filter describes a different result set, so paging
  // starts over rather than stranding the viewer mid-list.
  useEffect(() => {
    setPage(1);
    setExpandedPaymentId(null);
  }, [query, statusFilter]);

  const goToPage = (nextPage) => {
    const clamped = Math.min(Math.max(1, nextPage), pageCount);
    if (clamped === currentPage) return;

    // The expanded row belongs to the page being left, so it is collapsed to
    // avoid a detail panel lingering over a different set of payments.
    setExpandedPaymentId(null);
    setPage(clamped);
  };

  const handleDownload = async (purchase) => {
    if (!purchase.photo_id) return;

    setDownloadingId(purchase.id);
    try {
      const blob = await downloadPhoto(purchase.photo_id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = getPhotoName(purchase);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadReceipt = async (payment) => {
    if (!payment.transactionId) return;

    setDownloadingReceiptId(payment.id);
    try {
      const blob = await downloadReceipt(payment.transactionId);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `recu-${payment.transactionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Receipt download failed');
    } finally {
      setDownloadingReceiptId(null);
    }
  };

  if (loading) {
    return (
      <div className="payment-history-page">
        <div className="payment-history-loading">
          <div className="spinner"></div>
          <p>Loading payment history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-history-page">
      <section className="payment-history-header">
        <div>
          <p className="payment-history-kicker">
            {isPhotographer ? 'Photographer sales' : 'My payments'}
          </p>
          <h1>Payment History</h1>
          <p>
            {isPhotographer
              ? 'Review payments received for your event photos.'
              : 'Review your purchased photos, receipts, and download access.'}
          </p>
        </div>
        <FaReceipt className="payment-history-header-icon" />
      </section>

      {error && <div className="error-message payment-history-alert">{error}</div>}

      <section className="payment-history-stats">
        <div className="payment-stat">
          <span>Total payments</span>
          <strong>{totals.count}</strong>
        </div>
        <div className="payment-stat">
          <span>Completed payments</span>
          <strong>{totals.completed}</strong>
        </div>
        <div className="payment-stat">
          <span>{isPhotographer ? 'Revenus nets' : 'Total spent'}</span>
          <strong>{formatAmount(summary?.total_revenue ?? totals.total)}</strong>
        </div>
      </section>

      <section className="payment-history-controls">
        <label className="payment-search">
          <FaSearch />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={isPhotographer ? 'Search buyer, event, photo, transaction' : 'Search event, photo, transaction'}
          />
        </label>

        <div className="payment-status-tabs" aria-label="Filter by status">
          {['all', 'completed', 'pending', 'failed'].map(status => (
            <button
              key={status}
              type="button"
              className={statusFilter === status ? 'active' : ''}
              onClick={() => setStatusFilter(status)}
            >
              {status === 'all' ? 'All' : statusMeta[status].label}
            </button>
          ))}
        </div>
      </section>

      {filteredRecords.length === 0 ? (
        <section className="payment-empty-state">
          <FaCreditCard />
          <h2>No payments found</h2>
          <p>
            {records.length === 0
              ? isPhotographer
                ? 'Completed customer payments for your photos will appear here.'
                : 'Purchased photos will appear here after checkout.'
              : 'Try a different search or status filter.'}
          </p>
          {!isPhotographer && records.length === 0 && (
            <Link to="/events" className="payment-empty-action">Browse Events</Link>
          )}
        </section>
      ) : (
        <section className="payment-history-table-wrap">
          <table className="payment-history-table">
            <thead>
              <tr>
                <th>Photos included</th>
                <th>Events</th>
                {isPhotographer && <th>Buyer</th>}
                <th>Payment</th>
                <th>Date</th>
                <th>Status</th>
                <th className="amount-cell">Amount</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {pagedRecords.map((payment) => {
                const status = payment.status;
                const StatusIcon = statusMeta[status]?.icon || FaClock;
                const isExpanded = expandedPaymentId === payment.id;
                const visiblePhotoNames = payment.photoNames.join(', ');

                return (
                  <React.Fragment key={payment.id}>
                    <tr>
                      {/* Each cell's content sits in one .payment-cell wrapper so
                          the phone card layout can stack it beside the label. */}
                      <td data-label="Photos included" className="payment-photos-cell">
                        <div className="payment-cell">
                          <strong>{visiblePhotoNames}</strong>
                          <span>{payment.purchases.length} photo{payment.purchases.length > 1 ? 's' : ''} included</span>
                        </div>
                      </td>
                      <td data-label="Events">
                        <div className="payment-cell">{payment.eventNames.join(', ')}</div>
                      </td>
                      {isPhotographer && (
                        <td data-label="Buyer">
                          <div className="payment-cell">
                            <strong>{payment.buyer?.name || 'Customer'}</strong>
                            <span>{payment.buyer?.email || 'No email'}</span>
                          </div>
                        </td>
                      )}
                      {/* Method and transaction share a column: two separate
                          ones pushed the table past the page width. */}
                      <td data-label="Payment">
                        <div className="payment-cell">
                          <strong>{getPaymentLabel(payment.transactionId)}</strong>
                          <code title={payment.transactionId}>{payment.transactionId || 'N/A'}</code>
                        </div>
                      </td>
                      <td data-label="Date" className="payment-date-cell">
                        <div className="payment-cell">
                          <strong>{formatDate(payment.createdAt)}</strong>
                          {formatTimeGMT(payment.createdAt) && (
                            <span>{formatTimeGMT(payment.createdAt)}</span>
                          )}
                        </div>
                      </td>
                      <td data-label="Status">
                        <div className="payment-cell">
                          <span className={`payment-status ${status}`}>
                            <StatusIcon />
                            {statusMeta[status]?.label || status}
                          </span>
                        </div>
                      </td>
                      <td data-label="Amount" className="amount-cell">
                        <div className="payment-cell">{formatAmount(isPhotographer ? payment.netAmount : payment.amount)}
                          {isPhotographer && <small style={{ display: 'block' }}>Brut : {formatAmount(payment.amount)} · Commission {APP_NAME} : −{formatAmount(payment.commissionAmount)}</small>}
                        </div>
                      </td>
                      <td data-label="Details">
                        <div className="payment-row-actions payment-cell">
                          <button
                            type="button"
                            className="payment-detail-toggle"
                            aria-expanded={isExpanded}
                            onClick={() => setExpandedPaymentId(isExpanded ? null : payment.id)}
                          >
                            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                            <span>{isExpanded ? 'Hide' : 'View'}</span>
                          </button>
                          {!isPhotographer && status === 'completed' && (
                            <button
                              type="button"
                              className="download-receipt-button"
                              disabled={downloadingReceiptId === payment.id}
                              onClick={() => handleDownloadReceipt(payment)}
                            >
                              <FaReceipt />
                              <span>{downloadingReceiptId === payment.id ? 'Downloading' : 'Receipt'}</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="payment-detail-row">
                        <td colSpan={isPhotographer ? 8 : 7}>
                          <div className="payment-detail-panel">
                            <div className="payment-detail-heading">
                              <FaImages />
                              <div>
                                <strong>Payment details</strong>
                                <span>{payment.purchases.length} photo{payment.purchases.length > 1 ? 's' : ''} in this payment</span>
                              </div>
                            </div>

                            <div className="payment-detail-list">
                              {payment.purchases.map((purchase) => {
                                const purchaseStatus = normalizeStatus(purchase.payment_status);
                                const PurchaseStatusIcon = statusMeta[purchaseStatus]?.icon || FaClock;

                                return (
                                  <div className="payment-detail-item" key={purchase.id}>
                                    <div className="payment-detail-photo">
                                      <strong>{getPhotoName(purchase)}</strong>
                                      <span>{getEventName(purchase)} - Photo #{purchase.photo_id}</span>
                                    </div>
                                    <code title={purchase.transaction_id}>{purchase.transaction_id || 'N/A'}</code>
                                    <span className={`payment-status ${purchaseStatus}`}>
                                      <PurchaseStatusIcon />
                                      {statusMeta[purchaseStatus]?.label || purchaseStatus}
                                    </span>
                                    <strong className="payment-detail-amount">{formatAmount(isPhotographer ? (purchase.net_amount ?? purchase.amount) : purchase.amount)}</strong>
                                    {isPhotographer && <small style={{ display: 'block' }}>Brut : {formatAmount(purchase.amount)} · Commission {APP_NAME} ({Number(purchase.commission_percent || 0)} %) : −{formatAmount(purchase.commission_amount)}</small>}
                                    {!isPhotographer && (
                                      <button
                                        type="button"
                                        className="download-receipt-button"
                                        disabled={downloadingId === purchase.id || purchaseStatus !== 'completed'}
                                        onClick={() => handleDownload(purchase)}
                                      >
                                        <FaDownload />
                                        <span>{downloadingId === purchase.id ? 'Downloading' : 'Download'}</span>
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {pageCount > 1 && (
            <nav className="payment-pagination" aria-label="Payment history pages">
              <button
                type="button"
                className="payment-pagination-button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <FaChevronLeft /> Previous
              </button>

              <span className="payment-pagination-status">
                Page {currentPage} of {pageCount}
                <span>
                  {(currentPage - 1) * PAYMENTS_PER_PAGE + 1}
                  {'–'}
                  {Math.min(currentPage * PAYMENTS_PER_PAGE, filteredRecords.length)} of{' '}
                  {filteredRecords.length} payment{filteredRecords.length > 1 ? 's' : ''}
                </span>
              </span>

              <button
                type="button"
                className="payment-pagination-button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === pageCount}
              >
                Next <FaChevronRight />
              </button>
            </nav>
          )}
        </section>
      )}
    </div>
  );
}

export default PaymentHistory;
